import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { courseService } from '../../../services/courses.js';
import type {
  ModuleResponse,
  ModuleCreateInput,
  ModuleUpdateInput,
} from '../../../services/courses.js';
import { COURSE_KEYS } from './useCourses.js';

// ---------- Module queries ----------
export function useCourseModules(courseId: string | undefined) {
  return useQuery<ModuleResponse[]>({
    queryKey: COURSE_KEYS.modules(courseId ?? ''),
    queryFn: () => courseService.listModules(courseId!),
    enabled: !!courseId,
    staleTime: 1000 * 60 * 2,
  });
}

// ---------- Module mutations ----------
export function useCreateModule(courseId: string) {
  const qc = useQueryClient();
  return useMutation<ModuleResponse, Error, ModuleCreateInput>({
    mutationFn: (input) => courseService.createModule(courseId, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: COURSE_KEYS.modules(courseId) });
      qc.invalidateQueries({ queryKey: COURSE_KEYS.detail(courseId) });
    },
  });
}

export function useUpdateModule(courseId: string) {
  const qc = useQueryClient();
  return useMutation<ModuleResponse, Error, { moduleId: string; input: ModuleUpdateInput }>({
    mutationFn: ({ moduleId, input }) => courseService.updateModule(courseId, moduleId, input),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: COURSE_KEYS.modules(courseId) });
    },
  });
}

export function useDeleteModule(courseId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (moduleId) => courseService.deleteModule(courseId, moduleId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: COURSE_KEYS.modules(courseId) });
      qc.invalidateQueries({ queryKey: COURSE_KEYS.lessons(courseId) });
      qc.invalidateQueries({ queryKey: COURSE_KEYS.detail(courseId) });
    },
  });
}

export function useReorderModules(courseId: string) {
  const qc = useQueryClient();
  return useMutation<{ reordered: boolean }, Error, string[]>({
    mutationFn: (order) => courseService.reorderModules(courseId, order),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: COURSE_KEYS.modules(courseId) });
    },
  });
}
